import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight } from "lucide-react";
const ROLE_OPTIONS = ["Cleaner", "Sweeper", "Watchman", "Gardener", "Helper", "Cook", "Peon", "Driver", "Electrician", "Plumber", "Other"];
const STATUS_CONFIG = {
  Active: {
    bg: "#0D47A1",
    color: "#FFFFFF",
    dot: "#FFFFFF"
  },
  "On Leave": {
    bg: "#2196F3",
    color: "#FFFFFF",
    dot: "#FFFFFF"
  },
  Inactive: {
    bg: "#E3F2FD",
    color: "#0D47A1",
    dot: "#0D47A1"
  }
};
const BLOOD_GROUPS = ["A+", "A-", "B+", "B-", "AB+", "AB-", "O+", "O-"];
const emptyForm = {
  name: "",
  phone: "",
  altPhone: "",
  gender: "",
  dob: "",
  bloodGroup: "",
  role: "",
  joiningDate: new Date().toISOString().slice(0, 10),
  salary: "",
  aadhaar: "",
  photo: "",
  address: "",
  city: "",
  pincode: "",
  emergencyContactName: "",
  emergencyContactPhone: "",
  status: "Active",
  remarks: ""
};
function initials(name) {
  if (!name) return "?";
  return name.split(" ").slice(0, 2).map(w => w[0]).join("").toUpperCase();
}
const AVATAR_PALETTES = [{
  from: "#0D47A1",
  to: "#2196F3"
}, {
  from: "#2196F3",
  to: "#0D47A1"
}, {
  from: "#0D47A1",
  to: "#2196F3"
}, {
  from: "#2196F3",
  to: "#0D47A1"
}, {
  from: "#0D47A1",
  to: "#2196F3"
}, {
  from: "#2196F3",
  to: "#0D47A1"
}];
function Avatar({
  name,
  idx,
  size = 40,
  photo
}) {
  const p = AVATAR_PALETTES[(idx || 0) % AVATAR_PALETTES.length];
  if (photo) {
    return <img src={photo} alt={name} style={{
      width: size,
      height: size,
      borderRadius: size * 0.3,
      objectFit: "cover",
      flexShrink: 0,
      boxShadow: `0 4px 12px ${p.from}40`
    }} />;
  }
  return <div style={{
    width: size,
    height: size,
    borderRadius: size * 0.3,
    background: `linear-gradient(135deg, ${p.from}, ${p.to})`,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: size * 0.35,
    fontWeight: 700,
    color: "#FFFFFF",
    flexShrink: 0,
    letterSpacing: "-0.5px",
    boxShadow: `0 4px 12px ${p.from}40`
  }}>
      {initials(name)}
    </div>;
}
const inputBase = {
  width: "100%",
  background: "#FFFFFF",
  border: "none",
  borderBottom: "2px solid #0D47A1",
  borderRadius: 0,
  padding: "10px 14px 8px 14px",
  fontSize: 13,
  color: "#0D47A1",
  fontFamily: "'Inter', sans-serif",
  outline: "none",
  transition: "all 0.2s ease",
  boxSizing: "border-box"
};
function Inp({
  value,
  onChange,
  ...props
}) {
  const [f, setF] = useState(false);
  return <input value={value} onChange={onChange} {...props} style={{
    ...inputBase,
    borderBottomColor: f ? "#2196F3" : "#0D47A1"
  }} onFocus={() => setF(true)} onBlur={() => setF(false)} />;
}
function Sel({
  value,
  onChange,
  children,
  style: extra,
  ...props
}) {
  const [f, setF] = useState(false);
  return <select value={value} onChange={onChange} {...props} style={{
    ...inputBase,
    borderBottomColor: f ? "#2196F3" : "#0D47A1",
    cursor: "pointer",
    ...extra
  }} onFocus={() => setF(true)} onBlur={() => setF(false)}>
      {children}
    </select>;
}
function FLabel({
  label,
  required,
  children,
  err
}) {
  return <div style={{
    marginBottom: 16
  }}>
      <label style={{
      fontSize: 11,
      color: "#0D47A1",
      fontWeight: 600,
      display: "block",
      marginBottom: 6,
      textTransform: "uppercase",
      letterSpacing: "0.5px",
      fontFamily: "'Inter', sans-serif"
    }}>
        {label}{required && <span style={{
        color: "#0D47A1"
      }}> *</span>}
      </label>
      {children}
      {err && <div style={{
      fontSize: 10,
      color: "#0D47A1",
      marginTop: 4,
      fontFamily: "'Inter', sans-serif"
    }}>{err}</div>}
    </div>;
}
function SecHead({
  icon,
  title
}) {
  return <div style={{
    display: "flex",
    alignItems: "center",
    gap: 12,
    margin: "28px 0 16px"
  }}>
      <div style={{
      width: 4,
      height: 20,
      background: "linear-gradient(180deg, #0D47A1, #2196F3)",
      borderRadius: 2
    }} />
      <span style={{
      fontSize: 11,
      fontWeight: 700,
      color: "#0D47A1",
      textTransform: "uppercase",
      letterSpacing: "1px",
      fontFamily: "'Inter', sans-serif"
    }}>
        <i className={`fas ${icon}`} style={{
        marginRight: 8
      }}></i> {title}
      </span>
      <div style={{
      flex: 1,
      height: 1,
      background: "#2196F3"
    }} />
    </div>;
}
function DRow({
  label,
  value
}) {
  if (!value) return null;
  return <div style={{
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    padding: "10px 0",
    gap: 12
  }}>
      <span style={{
      fontSize: 11,
      color: "#0D47A1",
      flexShrink: 0,
      fontWeight: 500
    }}>{label}</span>
      <span style={{
      fontSize: 12,
      fontWeight: 600,
      color: "#0D47A1",
      textAlign: "right"
    }}>{value}</span>
    </div>;
}
function ConfirmDialog({
  message,
  onConfirm,
  onCancel
}) {
  return <div style={{
    position: "fixed",
    inset: 0,
    background: "rgba(13,71,161,0.5)",
    zIndex: 9999,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    backdropFilter: "blur(8px)"
  }}>
      <div style={{
      background: "#FFFFFF",
      borderRadius: 24,
      width: "min(400px, 90%)",
      padding: 32,
      textAlign: "center",
      boxShadow: "0 40px 80px rgba(0,0,0,0.15)"
    }}>
        <div style={{
        width: 56,
        height: 56,
        borderRadius: 20,
        background: "#E3F2FD",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        margin: "0 auto 20px",
        fontSize: 24
      }}>
          <i className="fas fa-exclamation-triangle" style={{
          color: "#0D47A1"
        }}></i>
        </div>
        <div style={{
        fontSize: 14,
        color: "#0D47A1",
        marginBottom: 28,
        lineHeight: 1.6
      }}>{message}</div>
        <div style={{
        display: "flex",
        gap: 12
      }}>
          <button onClick={onCancel} style={{
          flex: 1,
          background: "#FFFFFF",
          color: "#0D47A1",
          padding: 12,
          borderRadius: 12,
          fontSize: 13,
          fontWeight: 600,
          cursor: "pointer",
          boxShadow: "0 1px 3px rgba(0,0,0,0.1)"
        }}>Cancel</button>
          <button onClick={onConfirm} style={{
          flex: 1,
          background: "#0D47A1",
          color: "#FFFFFF",
          border: "none",
          padding: 12,
          borderRadius: 12,
          fontSize: 13,
          fontWeight: 600,
          cursor: "pointer"
        }}>Delete</button>
        </div>
      </div>
    </div>;
}
function DetailModal({
  worker,
  onClose,
  onDelete
}) {
  const [showConfirmDelete, setShowConfirmDelete] = useState(false);
  const confirmDelete = async () => {
    setShowConfirmDelete(false);
    try {
      const r = await fetch(`https://schoolcrm-fzcv.onrender.com/api/workers/${worker._id}`, {
        method: "DELETE"
      });
      const data = await r.json();
      if (!r.ok) {
        throw new Error(data.error || "Failed to delete worker");
      }
      onDelete(worker._id);
      onClose();
    } catch (err) {
      alert("Failed to delete worker: " + err.message);
    }
  };
  return <>
      <div style={{
      position: "fixed",
      inset: 0,
      background: "rgba(13,71,161,0.5)",
      zIndex: 2000,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      backdropFilter: "blur(8px)",
      padding: "20px 16px"
    }} onClick={onClose}>
        <div style={{
        background: "#FFFFFF",
        borderRadius: 28,
        width: "min(700px, 100%)",
        maxHeight: "90vh",
        overflow: "auto",
        boxShadow: "0 40px 80px rgba(0,0,0,0.1)",
        fontFamily: "'Inter', sans-serif"
      }} onClick={e => e.stopPropagation()}>
          <div style={{
          background: "linear-gradient(135deg, #0D47A1, #2196F3)",
          padding: "28px 32px",
          borderRadius: "28px 28px 0 0",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center"
        }}>
            <div style={{
            display: "flex",
            gap: 18,
            alignItems: "center"
          }}>
              <Avatar name={worker.name} idx={0} size={52} photo={worker.photo} />
              <div>
                <div style={{
                fontWeight: 800,
                fontSize: 18,
                color: "#FFFFFF"
              }}>{worker.name}</div>
                <div style={{
                fontSize: 12,
                color: "#FFFFFF",
                marginTop: 4,
                display: "flex",
                flexWrap: "wrap",
                gap: 8
              }}>
                  <span>{worker.role}</span>
                  <span>· Joined {worker.joiningDate}</span>
                </div>
              </div>
            </div>
            <div style={{
            display: "flex",
            gap: 12,
            alignItems: "center"
          }}>
              <button onClick={onClose} style={{
              background: "rgba(255,255,255,0.2)",
              border: "none",
              color: "#FFFFFF",
              borderRadius: 12,
              width: 36,
              height: 36,
              fontSize: 16,
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center"
            }}>
                <i className="fas fa-times"></i>
              </button>
            </div>
          </div>

          <div style={{
          padding: "32px"
        }}>
            <div style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))",
            gap: "0 40px"
          }}>
              <div>
                <SecHead icon="fa-id-badge" title="Personal Details" />
                <DRow label="Full Name" value={worker.name} />
                <DRow label="Role" value={worker.role} />
                <DRow label="Gender" value={worker.gender} />
                <DRow label="Date of Birth" value={worker.dob} />
                <DRow label="Blood Group" value={worker.bloodGroup} />
                <DRow label="Aadhaar No" value={worker.aadhaar} />
                <SecHead icon="fa-home" title="Address" />
                {worker.address ? <div style={{
                fontSize: 12,
                color: "#0D47A1",
                lineHeight: 1.8
              }}>{worker.address}<br />{worker.city}{worker.pincode ? ` – ${worker.pincode}` : ""}</div> : <div style={{
                fontSize: 12,
                color: "#0D47A1"
              }}>Not provided</div>}
              </div>
              <div>
                <SecHead icon="fa-briefcase" title="Employment Details" />
                <DRow label="Joining Date" value={worker.joiningDate} />
                <DRow label="Monthly Salary" value={worker.salary ? `₹${Number(worker.salary).toLocaleString()}` : ""} />
                <DRow label="Phone" value={worker.phone} />
                <DRow label="Alternate Phone" value={worker.altPhone} />
                <SecHead icon="fa-phone-alt" title="Emergency Contact" />
                <DRow label="Contact Name" value={worker.emergencyContactName} />
                <DRow label="Contact Phone" value={worker.emergencyContactPhone} />
              </div>
            </div>

            {worker.remarks && <>
                <SecHead icon="fa-pen" title="Remarks" />
                <div style={{
              fontSize: 13,
              color: "#0D47A1",
              background: "#E3F2FD",
              padding: "14px 18px",
              borderRadius: 14,
              lineHeight: 1.7
            }}>{worker.remarks}</div>
              </>}

            <div style={{
            display: "flex",
            gap: 12,
            alignItems: "center",
            flexWrap: "wrap",
            marginTop: 24
          }}>
              <button onClick={() => setShowConfirmDelete(true)} style={{
              marginLeft: "auto",
              background: "#FFFFFF",
              color: "#0D47A1",
              padding: "10px 18px",
              borderRadius: 12,
              fontSize: 12,
              fontWeight: 700,
              cursor: "pointer",
              boxShadow: "0 1px 3px rgba(0,0,0,0.1)"
            }}>
                <i className="fas fa-trash" style={{
                marginRight: 6
              }}></i>Remove Worker
              </button>
            </div>
          </div>
        </div>
      </div>
      {showConfirmDelete && <ConfirmDialog message={`Remove ${worker.name} from the workers list?`} onConfirm={confirmDelete} onCancel={() => setShowConfirmDelete(false)} />}
    </>;
}
export default function WorkersPage() {
  const [workers, setWorkers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(emptyForm);
  const [filterStatus, setFilterStatus] = useState("All");
  const [filterRole, setFilterRole] = useState("All");
  const [search, setSearch] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [viewWorker, setViewWorker] = useState(null);
  const [deleteWorker, setDeleteWorker] = useState(null);
  const [errors, setErrors] = useState({});
  const [success, setSuccess] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [apiError, setApiError] = useState(null);
  const [onLeaveWorkerIds, setOnLeaveWorkerIds] = useState([]);
  const navigate = useNavigate();
  const API_BASE = "https://schoolcrm-fzcv.onrender.com/api";
  const fetchWorkers = async () => {
    setLoading(true);
    setApiError(null);
    try {
      const queryParams = new URLSearchParams();
      if (filterStatus !== 'All') queryParams.append('status', filterStatus);
      if (filterRole !== 'All') queryParams.append('role', filterRole);
      if (search.trim()) queryParams.append('search', search.trim());
      const url = `${API_BASE}/workers${queryParams.toString() ? '?' + queryParams.toString() : ''}`;
      const r = await fetch(url);
      if (!r.ok) {
        const data = await r.json();
        throw new Error(data.error || 'Failed to fetch workers');
      }
      const data = await r.json();
      setWorkers(data);
    } catch (err) {
      console.error('Fetch error:', err);
      setApiError(err.message);
      setWorkers([]);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    fetchWorkers();
  }, []);

  // Refetch when filters change
  useEffect(() => {
    fetchWorkers();
  }, [filterStatus, filterRole, search]);

  // Fetch today's approved worker leaves whenever "On Leave" filter is selected
  const fetchOnLeaveToday = async () => {
    try {
      const today = new Date().toISOString().slice(0, 10);
      const r = await fetch(`${API_BASE}/leave-requests/date/${today}?type=Worker&status=approved`);
      if (!r.ok) throw new Error("Failed to fetch today's leave requests");
      const data = await r.json();
      setOnLeaveWorkerIds((Array.isArray(data) ? data : []).map(l => String(l.workerId)));
    } catch (err) {
      console.error("fetchOnLeaveToday error:", err);
      setOnLeaveWorkerIds([]);
    }
  };
  useEffect(() => {
    if (filterStatus === "On Leave") fetchOnLeaveToday();
  }, [filterStatus]);
  const setField = field => e => {
    const val = e && e.target ? e.target.value : e;
    setForm(prev => ({
      ...prev,
      [field]: val
    }));
    if (errors[field]) setErrors(er => ({
      ...er,
      [field]: null
    }));
  };
  const handlePhotoChange = e => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      setForm(prev => ({
        ...prev,
        photo: reader.result
      }));
    };
    reader.readAsDataURL(file);
  };
  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = "Required";
    if (!form.phone || !/^\d{10}$/.test(form.phone)) e.phone = "Valid 10-digit mobile needed";
    if (!form.gender) e.gender = "Required";
    if (!form.role) e.role = "Required";
    if (!form.joiningDate) e.joiningDate = "Required";
    return e;
  };
  const handleSubmit = async () => {
    const e = validate();
    if (Object.keys(e).length) {
      setErrors(e);
      return;
    }
    setSubmitting(true);
    setApiError(null);

    // Clean form data - remove empty fields
    const cleanForm = {
      ...form
    };
    Object.keys(cleanForm).forEach(key => {
      if (cleanForm[key] === '') {
        delete cleanForm[key];
      }
    });
    try {
      const response = await fetch(`${API_BASE}/workers`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json"
        },
        body: JSON.stringify(cleanForm)
      });
      const data = await response.json();
      if (!response.ok) {
        // Handle specific error cases
        if (response.status === 400) {
          if (data.details) {
            // Validation errors
            const fieldErrors = {};
            Object.keys(data.details).forEach(field => {
              fieldErrors[field] = data.details[field];
            });
            setErrors(fieldErrors);
            throw new Error(data.message || 'Please check all fields');
          }
          throw new Error(data.error || 'Validation failed');
        }
        if (response.status === 409) {
          throw new Error(data.error || 'Duplicate entry');
        }
        throw new Error(data.error || 'Failed to submit');
      }

      // Success
      setWorkers(prev => [data, ...prev]);
      setForm(emptyForm);
      setErrors({});
      setSuccess(true);
      setTimeout(() => setSuccess(false), 3500);
      setShowForm(false);
    } catch (err) {
      console.error('Submit error:', err);
      setApiError(err.message);
      // Don't show alert for validation errors (they're shown in form)
      if (!errors || Object.keys(errors).length === 0) {
        alert("Failed to submit: " + err.message);
      }
    } finally {
      setSubmitting(false);
    }
  };
  const removeWorker = id => {
    setWorkers(prev => prev.filter(w => w._id !== id));
  };
  const confirmDeleteWorker = async () => {
    if (!deleteWorker) return;
    const id = deleteWorker._id;
    setDeleteWorker(null);
    try {
      const r = await fetch(`${API_BASE}/workers/${id}`, {
        method: "DELETE"
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.error || data.message || "Failed to delete worker");
      removeWorker(id);
    } catch (err) {
      alert("Failed to delete worker: " + err.message);
    }
  };
  const filtered = filterStatus === "On Leave" ? workers.filter(w => onLeaveWorkerIds.includes(String(w._id))) : workers;
  const summary = [{
    label: "Total Workers",
    key: "All",
    color: "#0D47A1",
    light: "#E3F2FD",
    icon: "fa-users"
  }, {
    label: "Active",
    key: "Active",
    color: "#0D47A1",
    light: "#E3F2FD",
    icon: "fa-check-circle"
  }, {
    label: "On Leave",
    key: "On Leave",
    color: "#2196F3",
    light: "#E3F2FD",
    icon: "fa-calendar-times"
  }, {
    label: "Inactive",
    key: "Inactive",
    color: "#E3F2FD",
    light: "#E3F2FD",
    icon: "fa-times-circle"
  }];
  const total = workers.length || 1;
  if (loading) return <div style={{
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    height: "100vh",
    background: "#FFFFFF",
    fontFamily: "'Inter', sans-serif"
  }}>
      <div style={{
      textAlign: "center"
    }}>
        <div style={{
        width: 64,
        height: 64,
        borderRadius: 20,
        background: "linear-gradient(135deg, #0D47A1, #2196F3)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontSize: 28,
        margin: "0 auto 20px",
        boxShadow: "0 8px 24px rgba(13,71,161,0.3)"
      }}>
          <i className="fas fa-spinner fa-pulse" style={{
          color: "#FFFFFF"
        }}></i>
        </div>
        <div style={{
        color: "#0D47A1"
      }}>Loading workers...</div>
      </div>
    </div>;
  return <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700;800&display=swap');
        @import url('https://cdnjs.cloudflare.com/ajax/libs/font-awesome/6.0.0-beta3/css/all.min.css');
        *, *::before, *::after { margin: 0; padding: 0; box-sizing: border-box; }
        body { background: #FFFFFF; font-family: 'Inter', sans-serif; }
        select option { background: #FFFFFF; color: #0D47A1; }
        ::-webkit-scrollbar { width: 6px; }
        ::-webkit-scrollbar-track { background: #FFFFFF; }
        ::-webkit-scrollbar-thumb { background: #2196F3; border-radius: 6px; }
        @keyframes fadeUp { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes slideIn { from { opacity: 0; transform: scale(0.96); } to { opacity: 1; transform: scale(1); } }
        .pipe-card { transition: all 0.25s ease; cursor: pointer; }
        .pipe-card:hover { transform: translateY(-4px); box-shadow: 0 12px 32px rgba(13,71,161,0.08) !important; }
        .trow { transition: background 0.2s ease; }
        .trow:hover { background: #E3F2FD !important; }
        .action-btn { transition: all 0.2s ease; }
        .action-btn:hover { transform: translateY(-1px); filter: brightness(0.95); }
        .form-slide { animation: slideIn 0.25s ease; }
        .nav-btn { transition: all 0.2s ease; }
        .nav-btn:hover { transform: translateY(-2px); filter: brightness(1.05); }
        @media (max-width: 768px) {
          .hide-mobile { display: none; }
          .pipeline-grid { gap: 12px !important; }
          .table-header, .table-row { grid-template-columns: 1.6fr 1fr 1fr 0.9fr 110px !important; gap: 8px !important; }
          .table-row { padding: 12px 16px !important; }
        }
        @media (max-width: 640px) {
          .table-header, .table-row { grid-template-columns: 1fr !important; gap: 8px !important; }
          .table-header { display: none; }
          .table-row { border-radius: 16px; margin-bottom: 12px; background: #FFFFFF; padding: 16px !important; box-shadow: 0 1px 3px rgba(0,0,0,0.05); }
          .table-row > div { display: flex; justify-content: space-between; align-items: center; }
          .table-row > div::before { content: attr(data-label); font-size: 10px; font-weight: 600; color: #0D47A1; text-transform: uppercase; letter-spacing: 0.5px; }
          .action-btns { justify-content: flex-end !important; }
        }
      `}</style>

      <div style={{
      minHeight: "100vh",
      background: "#FFFFFF"
    }}>

        {/* ═══ HEADER ═══ */}
        <div style={{
        background: "linear-gradient(135deg, #0D47A1, #2196F3)",
        padding: "14px 24px",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        position: "sticky",
        top: 0,
        zIndex: 100,
        boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
        flexWrap: "wrap",
        gap: 12
      }}>
          <div style={{
          display: "flex",
          alignItems: "center",
          gap: 12
        }}>
           <div style={{
  width: 40,
  height: 40,
  borderRadius: 14,
  background: "#FFFFFF",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  boxShadow: "0 4px 12px rgba(13,71,161,0.3)",
  overflow: "hidden"
}}>
  <img 
    src="/school-logo.png" 
    alt="School Logo"
    style={{
      width: "100%",
      height: "100%",
      objectFit: "contain"
    }}
  />
</div>
            <div>
              <div style={{
              fontWeight: 800,
              fontSize: 16,
              color: "#FFFFFF",
              letterSpacing: "-0.3px"
            }}>Workers Management</div>
              <div style={{
              fontSize: 9,
              color: "#FFFFFF",
              letterSpacing: "0.6px",
              textTransform: "uppercase",
              fontWeight: 600
            }}>Support Staff · 2025–26</div>
            </div>
          </div>

          <div style={{
          display: "flex",
          gap: 8,
          flexWrap: "wrap",
          alignItems: "center"
        }}>
            {success && <div style={{
            background: "#0D47A1",
            color: "#FFFFFF",
            padding: "4px 12px",
            borderRadius: 12,
            fontSize: 10,
            fontWeight: 600,
            animation: "fadeUp 0.3s ease",
            display: "flex",
            alignItems: "center",
            gap: 4
          }}>
                <i className="fas fa-check-circle"></i> Worker added!
              </div>}
            {apiError && <div style={{
            background: "#FFFFFF",
            color: "#0D47A1",
            padding: "4px 12px",
            borderRadius: 12,
            fontSize: 10,
            fontWeight: 600,
            display: "flex",
            alignItems: "center",
            gap: 4,
            boxShadow: "0 1px 3px rgba(0,0,0,0.1)"
          }}>
                <i className="fas fa-exclamation-circle"></i> {apiError}
              </div>}
            <button onClick={() => navigate("/worker-attendance-page")} className="nav-btn" style={{
            background: "#FFFFFF",
            color: "#0D47A1",
            padding: "6px 14px",
            borderRadius: 10,
            fontSize: 11,
            fontWeight: 600,
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            gap: 6,
            boxShadow: "0 1px 3px rgba(0,0,0,0.08)"
          }}>
              <i className="fas fa-user-check"></i> <span className="hide-mobile">Worker Attendance</span>
            </button>
            <button onClick={() => setShowForm(true)} className="nav-btn" style={{
            background: "linear-gradient(135deg, #0D47A1, #2196F3)",
            color: "#FFFFFF",
            border: "none",
            padding: "6px 16px",
            borderRadius: 10,
            fontSize: 11,
            fontWeight: 700,
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            gap: 6,
            boxShadow: "0 4px 10px rgba(13,71,161,0.3)"
          }}>
              <i className="fas fa-user-plus"></i> <span className="hide-mobile">Add Worker</span>
            </button>
            <button onClick={() => navigate("/admin")} className="nav-btn" style={{
            background: "#FFFFFF",
            color: "#0D47A1",
            padding: "6px 14px",
            borderRadius: 10,
            fontSize: 11,
            fontWeight: 600,
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            gap: 6,
            boxShadow: "0 1px 3px rgba(0,0,0,0.08)"
          }}>
              <i className="fas fa-arrow-left"></i> <span className="hide-mobile">Back</span>
            </button>
          </div>
        </div>

        {/* ═══ SUMMARY TILES ═══ */}
        <div style={{
        padding: "20px 24px 0"
      }}>
          <div className="pipeline-grid" style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))",
          gap: 16
        }}>
            {summary.map(p => {
            const count = p.key === "All" ? workers.length : workers.filter(w => w.status === p.key).length;
            const active = filterStatus === p.key;
            return <div key={p.key} className="pipe-card" onClick={() => setFilterStatus(active ? "All" : p.key)} style={{
              background: active ? p.light : "#FFFFFF",
              borderRadius: 20,
              padding: "16px 18px",
              boxShadow: active ? `0 8px 24px ${p.color}20` : "0 2px 8px rgba(0,0,0,0.04)"
            }}>
                  <div style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "flex-start",
                marginBottom: 12
              }}>
                    <div style={{
                  width: 36,
                  height: 36,
                  borderRadius: 12,
                  background: active ? p.color : "#0D47A1",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: 16,
                  transition: "all 0.2s",
                  boxShadow: active ? `0 4px 12px ${p.color}40` : "none"
                }}>
                      <i className={`fas ${p.icon}`} style={{
                    color: "#FFFFFF"
                  }}></i>
                    </div>
                    <div style={{
                  fontSize: 28,
                  fontWeight: 800,
                  color: active ? p.color : "#0D47A1",
                  lineHeight: 1
                }}>{count}</div>
                  </div>
                  <div style={{
                fontSize: 10,
                color: active ? p.color : "#0D47A1",
                fontWeight: 700,
                textTransform: "uppercase",
                letterSpacing: "0.5px"
              }}>{p.label}</div>
                  <div style={{
                marginTop: 10,
                height: 3,
                background: "#2196F3",
                borderRadius: 3
              }}>
                    <div style={{
                  width: `${Math.max(6, count / total * 100)}%`,
                  height: "100%",
                  background: active ? p.color : "#0D47A1",
                  borderRadius: 3,
                  transition: "width 0.6s ease"
                }} />
                  </div>
                </div>;
          })}
          </div>
        </div>

        {/* ═══ TABLE ═══ */}
        <div style={{
        padding: "16px 24px 40px"
      }}>
          <div style={{
          background: "#FFFFFF",
          borderRadius: 24,
          overflow: "hidden",
          boxShadow: "0 2px 12px rgba(0,0,0,0.04)"
        }}>
            <div style={{
            padding: "16px 20px",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexWrap: "wrap",
            gap: 12
          }}>
              <div style={{
              fontWeight: 800,
              fontSize: 14,
              color: "#0D47A1"
            }}>
                All Workers <span style={{
                fontWeight: 400,
                fontSize: 11,
                color: "#0D47A1"
              }}>({filtered.length})</span>
              </div>
              <div style={{
              display: "flex",
              gap: 10,
              flexWrap: "wrap",
              alignItems: "center"
            }}>
                <Inp value={search} onChange={e => setSearch(e.target.value)} placeholder="Search name or phone..." style={{
                width: 200
              }} />
                <Sel value={filterRole} onChange={e => setFilterRole(e.target.value)} style={{
                width: "auto",
                minWidth: 140
              }}>
                  <option value="All">All Roles</option>
                  {ROLE_OPTIONS.map(r => <option key={r}>{r}</option>)}
                </Sel>
                <div style={{
                display: "flex",
                gap: 6,
                flexWrap: "wrap"
              }}>
                  {["All", "On Leave"].map(s => {
                  const active = filterStatus === s;
                  return <button key={s} onClick={() => setFilterStatus(s)} style={{
                    padding: "4px 12px",
                    borderRadius: 20,
                    fontSize: 10,
                    cursor: "pointer",
                    fontWeight: active ? 700 : 500,
                    transition: "all 0.2s",
                    background: active ? "linear-gradient(135deg, #0D47A1, #2196F3)" : "transparent",
                    color: active ? "#FFFFFF" : "#0D47A1"
                  }}>
                        {s}
                      </button>;
                })}
                </div>
              </div>
            </div>

            <div className="table-header" style={{
            display: "grid",
            gridTemplateColumns: "2.2fr 1.1fr 1fr 1fr 130px",
            gap: 10,
            padding: "10px 20px",
            background: "linear-gradient(135deg, #0D47A1, #2196F3)"
          }}>
              {["Worker", "Role", "Phone", "Joined", "Actions"].map(h => <div key={h} style={{
              fontSize: 9,
              fontWeight: 700,
              color: "#FFFFFF",
              textTransform: "uppercase",
              letterSpacing: "1px"
            }}>{h}</div>)}
            </div>

            {filtered.length === 0 && <div style={{
            padding: 48,
            textAlign: "center"
          }}>
                <i className="fas fa-user-hard-hat" style={{
              fontSize: 40,
              color: "#2196F3",
              display: "block",
              marginBottom: 12
            }}></i>
                <div style={{
              color: "#0D47A1",
              fontSize: 13
            }}>No workers found</div>
              </div>}

            {filtered.map((w, idx) => <div key={w._id} className="table-row trow" style={{
            display: "grid",
            gridTemplateColumns: "2.2fr 1.1fr 1fr 1fr 130px",
            gap: 10,
            padding: "12px 20px",
            alignItems: "center",
            background: "#FFFFFF"
          }}>
                <div data-label="Worker" style={{
              display: "flex",
              alignItems: "center",
              gap: 12
            }}>
                  <Avatar name={w.name} idx={idx} size={36} photo={w.photo} />
                  <div>
                    <div style={{
                  fontSize: 13,
                  fontWeight: 700,
                  color: "#0D47A1"
                }}>{w.name}</div>
                    <div style={{
                  fontSize: 10,
                  color: "#0D47A1",
                  marginTop: 2
                }}>{w.gender || "—"}</div>
                  </div>
                </div>
                <div data-label="Role" style={{
              fontSize: 11,
              color: "#0D47A1",
              fontWeight: 500
            }}>{w.role}</div>
                <div data-label="Phone" style={{
              fontSize: 11,
              color: "#0D47A1"
            }}>{w.phone || "—"}</div>
                <div data-label="Joined" style={{
              fontSize: 11,
              color: "#0D47A1"
            }}>{w.joiningDate}</div>
                <div data-label="Actions" className="action-btns" style={{
              display: "flex",
              gap: 6
            }}>
                  <button className="action-btn" onClick={() => setViewWorker(w)} style={{
                background: "linear-gradient(135deg, #0D47A1, #2196F3)",
                color: "#FFFFFF",
                border: "none",
                padding: "5px 10px",
                borderRadius: 8,
                fontSize: 10,
                cursor: "pointer",
                fontWeight: 700
              }}>
                    View <ArrowRight className="inline w-[1em] h-[1em] align-[-0.125em]" />
                  </button>
                  <button className="action-btn" onClick={() => setDeleteWorker(w)} style={{
                background: "#FFFFFF",
                color: "#0D47A1",
                border: "none",
                padding: "5px 10px",
                borderRadius: 8,
                fontSize: 10,
                cursor: "pointer",
                fontWeight: 700,
                boxShadow: "0 1px 3px rgba(0,0,0,0.1)"
              }}>
                    <i className="fas fa-trash"></i>
                  </button>
                </div>
              </div>)}
          </div>
        </div>
      </div>

      {/* ═══ MODALS ═══ */}
      {viewWorker && <DetailModal worker={viewWorker} onClose={() => setViewWorker(null)} onDelete={removeWorker} />}
      {deleteWorker && <ConfirmDialog message={`Remove ${deleteWorker.name} from the workers list?`} onConfirm={confirmDeleteWorker} onCancel={() => setDeleteWorker(null)} />}

      {/* ═══ NEW WORKER FORM ═══ */}
      {showForm && <div style={{
      position: "fixed",
      inset: 0,
      background: "rgba(13,71,161,0.5)",
      zIndex: 1500,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      backdropFilter: "blur(8px)",
      padding: "20px 12px"
    }}>
          <div className="form-slide" style={{
        background: "#FFFFFF",
        borderRadius: 28,
        width: "min(750px, 100%)",
        maxHeight: "calc(100vh - 40px)",
        display: "flex",
        flexDirection: "column",
        boxShadow: "0 40px 80px rgba(0,0,0,0.1)"
      }}>
            <div style={{
          background: "linear-gradient(135deg, #0D47A1, #2196F3)",
          padding: "20px 24px",
          borderRadius: "28px 28px 0 0",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexShrink: 0
        }}>
              <div>
                <div style={{
              fontWeight: 800,
              fontSize: 16,
              color: "#FFFFFF"
            }}>
                  <i className="fas fa-user-plus" style={{
                marginRight: 8
              }}></i> Add New Worker
                </div>
                <div style={{
              fontSize: 11,
              color: "#FFFFFF",
              marginTop: 2
            }}>Support Staff Registration</div>
              </div>
              <button onClick={() => {
            setShowForm(false);
            setErrors({});
          }} style={{
            background: "rgba(255,255,255,0.2)",
            border: "none",
            color: "#FFFFFF",
            borderRadius: 10,
            width: 34,
            height: 34,
            fontSize: 16,
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            justifyContent: "center"
          }}>
                <i className="fas fa-times"></i>
              </button>
            </div>

            <div style={{
          overflowY: "auto",
          flex: 1,
          padding: "24px"
        }}>
              <SecHead icon="fa-id-badge" title="Personal Details" />
              <div style={{
            display: "flex",
            alignItems: "center",
            gap: 16,
            marginBottom: 16
          }}>
                <Avatar name={form.name} idx={0} size={64} photo={form.photo} />
                <div>
                  <label style={{
                fontSize: 11,
                color: "#0D47A1",
                fontWeight: 600,
                textTransform: "uppercase",
                letterSpacing: "0.5px",
                fontFamily: "'Inter', sans-serif",
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
                cursor: "pointer",
                background: "#E3F2FD",
                padding: "8px 14px",
                borderRadius: 10
              }}>
                    <i className="fas fa-camera"></i> {form.photo ? "Change Photo" : "Upload Photo"}
                    <input type="file" accept="image/*" onChange={handlePhotoChange} style={{
                  display: "none"
                }} />
                  </label>
                </div>
              </div>
              <div style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
            gap: "0 20px"
          }}>
                <FLabel label="Full Name" required err={errors.name}><Inp value={form.name} onChange={setField("name")} placeholder="Enter full name" /></FLabel>
                <FLabel label="Gender" required err={errors.gender}><Sel value={form.gender} onChange={setField("gender")}><option value="">Select Gender</option><option>Male</option><option>Female</option><option>Other</option></Sel></FLabel>
                <FLabel label="Date of Birth"><Inp type="date" value={form.dob} onChange={setField("dob")} /></FLabel>
                <FLabel label="Blood Group"><Sel value={form.bloodGroup} onChange={setField("bloodGroup")}><option value="">Select</option>{BLOOD_GROUPS.map(b => <option key={b}>{b}</option>)}</Sel></FLabel>
                <FLabel label="Aadhaar Number"><Inp value={form.aadhaar} onChange={setField("aadhaar")} placeholder="XXXX-XXXX-XXXX" maxLength={14} /></FLabel>
              </div>

              <SecHead icon="fa-briefcase" title="Employment Details" />
              <div style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
            gap: "0 20px"
          }}>
                <FLabel label="Role / Designation" required err={errors.role}><Sel value={form.role} onChange={setField("role")}><option value="">Select Role</option>{ROLE_OPTIONS.map(r => <option key={r}>{r}</option>)}</Sel></FLabel>
                <FLabel label="Joining Date" required err={errors.joiningDate}><Inp type="date" value={form.joiningDate} onChange={setField("joiningDate")} /></FLabel>
                <FLabel label="Monthly Salary (₹)"><Inp type="number" value={form.salary} onChange={setField("salary")} placeholder="e.g. 12000" /></FLabel>
              </div>

              <SecHead icon="fa-phone-alt" title="Contact Details" />
              <div style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
            gap: "0 20px"
          }}>
                <FLabel label="Mobile Number" required err={errors.phone}><Inp value={form.phone} onChange={setField("phone")} placeholder="10-digit mobile" maxLength={10} type="tel" /></FLabel>
                <FLabel label="Alternate Phone"><Inp value={form.altPhone} onChange={setField("altPhone")} placeholder="Optional" maxLength={10} type="tel" /></FLabel>
                <FLabel label="Emergency Contact Name"><Inp value={form.emergencyContactName} onChange={setField("emergencyContactName")} placeholder="Name" /></FLabel>
                <FLabel label="Emergency Contact Phone"><Inp value={form.emergencyContactPhone} onChange={setField("emergencyContactPhone")} placeholder="10-digit mobile" maxLength={10} type="tel" /></FLabel>
              </div>

              <SecHead icon="fa-home" title="Address" />
              <FLabel label="Full Address">
                <textarea value={form.address} onChange={setField("address")} placeholder="Door No, Street, Area" rows={2} style={{
              ...inputBase,
              resize: "vertical"
            }} />
              </FLabel>
              <div style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
            gap: "0 20px"
          }}>
                <FLabel label="City / Town"><Inp value={form.city} onChange={setField("city")} placeholder="City" /></FLabel>
                <FLabel label="Pincode"><Inp value={form.pincode} onChange={setField("pincode")} placeholder="6-digit pincode" maxLength={6} type="tel" /></FLabel>
              </div>

              <SecHead icon="fa-pen" title="Remarks" />
              <FLabel label="Additional Notes / Remarks">
                <textarea value={form.remarks} onChange={setField("remarks")} placeholder="Any additional remarks..." rows={2} style={{
              ...inputBase,
              resize: "vertical"
            }} />
              </FLabel>

              <div style={{
            display: "flex",
            gap: 10,
            marginTop: 24
          }}>
                <button onClick={() => {
              setShowForm(false);
              setErrors({});
            }} style={{
              flex: 1,
              background: "#FFFFFF",
              color: "#0D47A1",
              padding: 12,
              borderRadius: 14,
              fontSize: 12,
              fontWeight: 600,
              cursor: "pointer",
              boxShadow: "0 1px 3px rgba(0,0,0,0.08)"
            }}>Cancel</button>
                <button onClick={handleSubmit} disabled={submitting} style={{
              flex: 2,
              background: submitting ? "#2196F3" : "linear-gradient(135deg, #0D47A1, #2196F3)",
              color: "#FFFFFF",
              border: "none",
              padding: 12,
              borderRadius: 14,
              fontSize: 13,
              fontWeight: 800,
              cursor: submitting ? "not-allowed" : "pointer",
              boxShadow: submitting ? "none" : "0 4px 14px rgba(13,71,161,0.3)",
              transition: "all 0.2s"
            }}>
                  {submitting ? "Saving..." : "Add Worker"}
                </button>
              </div>
            </div>
          </div>
        </div>}
    </>;
}